import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Form,
  Row,
  Col,
  Table,
  Spinner,
  Button,
  Badge,
  OverlayTrigger,
  Popover,
  Modal,
} from 'react-bootstrap';
import PageHeader from 'components/PageHeader';
import { debounce } from 'lodash';
import RemoveSpaceAndDot from 'utils/RemoveSpaceAndDot';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import {
  searchBrregUpdates,
  BrregInfoUpdateDto,
  triggerBrregUpdate,
  getBrregJobsDashboard,
  BrregJobsDashboardResponse,
  runBrregJobNow,
  BrregJobName,
  getJobRuns,
  getJobRunOutcomes,
  BrregJobRunDto,
  BrregJobRunLogEntry,
  BrregEnhetOutcomesDto,
} from 'services/BrregOppdateringer';

// Component
const BrregOppdateringerPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<BrregJobsDashboardResponse | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [runNowLoadingJob, setRunNowLoadingJob] = useState<string | null>(null);
  const [runNowError, setRunNowError] = useState<string | null>(null);

  // Modal state for "Kjør nå"
  const [showRunNowModal, setShowRunNowModal] = useState(false);
  const [runNowModalJob, setRunNowModalJob] = useState<string | null>(null);
  const [runNowModalDate, setRunNowModalDate] = useState<Date | undefined>(undefined);

  const [orgnrQuery, setOrgnrQuery] = useState('');
  const [valueQuery, setValueQuery] = useState('');
  const [fieldQuery, setFieldQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50; // fixed as per provided endpoint example
  const [data, setData] = useState<BrregInfoUpdateDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastReqIdRef = useRef(0);

  // Modal state for manual trigger
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [triggerInput, setTriggerInput] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [triggerSuccess, setTriggerSuccess] = useState(false);

  // Modal state for job run details
  const [showRunsModal, setShowRunsModal] = useState(false);
  const [runsModalJob, setRunsModalJob] = useState<string | null>(null);
  const [jobRuns, setJobRuns] = useState<BrregJobRunDto[]>([]);
  const [jobRunsLoading, setJobRunsLoading] = useState(false);
  const [jobRunsError, setJobRunsError] = useState<string | null>(null);
  const [expandedRunIds, setExpandedRunIds] = useState<Set<string>>(new Set());
  const [outcomesCache, setOutcomesCache] = useState<Record<string, BrregEnhetOutcomesDto>>({});
  const [outcomesLoading, setOutcomesLoading] = useState<Set<string>>(new Set());

  const handleOpenRunsModal = useCallback(async (jobName: string) => {
    setRunsModalJob(jobName);
    setShowRunsModal(true);
    setJobRuns([]);
    setJobRunsError(null);
    setJobRunsLoading(true);
    try {
      const runs = await getJobRuns(jobName);
      setJobRuns(runs);
    } catch (e: any) {
      setJobRunsError(e?.message || 'Kunne ikke laste kjøringer');
    } finally {
      setJobRunsLoading(false);
    }
  }, []);

  const handleCloseRunsModal = useCallback(() => {
    setShowRunsModal(false);
    setRunsModalJob(null);
    setJobRuns([]);
    setJobRunsError(null);
    setExpandedRunIds(new Set());
    setOutcomesCache({});
    setOutcomesLoading(new Set());
  }, []);

  const toggleRunExpanded = useCallback((key: string, jobName: string, runId: string) => {
    setExpandedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Lazy-load outcomes for enhet-updates runs
        if (jobName === 'brreg-enhet-updates' && runId) {
          setOutcomesLoading((ls) => new Set(ls).add(runId));
          getJobRunOutcomes(jobName, runId)
            .then((data) => {
              setOutcomesCache((c) => ({ ...c, [runId]: data }));
            })
            .catch(() => {
              /* silently handled — no data shown */
            })
            .finally(() => {
              setOutcomesLoading((ls) => {
                const next2 = new Set(ls);
                next2.delete(runId);
                return next2;
              });
            });
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setDashboardLoading(true);
      setDashboardError(null);
      try {
        const json = await getBrregJobsDashboard();
        if (!cancelled) setDashboard(json);
      } catch (e: any) {
        if (!cancelled) setDashboardError(e?.message || 'Ukjent feil');
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const json = await getBrregJobsDashboard();
      setDashboard(json);
    } catch (e: any) {
      setDashboardError(e?.message || 'Ukjent feil');
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const supportedJobNames = useMemo(() => {
    const names: BrregJobName[] = [
      'brreg-enhet-updates',
      'brreg-role-updates',
      'brreg-underenhet-updates',
      'brreg-nye-enheter-nace',
    ];
    return new Set<string>(names);
  }, []);

  const handleRunNow = useCallback(
    async (jobName: string, fromDate?: Date) => {
      setRunNowError(null);
      setRunNowLoadingJob(jobName);
      try {
        const minDato = fromDate
          ? `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`
          : undefined;
        await runBrregJobNow(jobName as BrregJobName, minDato);
        await reloadDashboard();
      } catch (e: any) {
        setRunNowError(e?.message || 'Kunne ikke starte jobben');
      } finally {
        setRunNowLoadingJob(null);
      }
    },
    [reloadDashboard],
  );

  const handleOpenRunNowModal = useCallback((jobName: string) => {
    setRunNowModalJob(jobName);
    setRunNowModalDate(undefined);
    setRunNowError(null);
    setShowRunNowModal(true);
  }, []);

  const handleCloseRunNowModal = useCallback(() => {
    setShowRunNowModal(false);
    setRunNowModalJob(null);
    setRunNowModalDate(undefined);
  }, []);

  const handleRunNowConfirm = useCallback(async () => {
    if (!runNowModalJob) return;
    setShowRunNowModal(false);
    await handleRunNow(runNowModalJob, runNowModalDate);
  }, [runNowModalJob, runNowModalDate, handleRunNow]);

  const fetchData = useCallback(async () => {
    const reqId = ++lastReqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const json = await searchBrregUpdates({
        page,
        pageSize,
        orgNr: orgnrQuery.trim() || undefined,
        value: valueQuery.trim() || undefined,
        propertyName: fieldQuery.trim() || undefined,
      });
      if (lastReqIdRef.current !== reqId) return; // stale
      setData(json.items ?? []);
      setTotal(json.total ?? 0);
    } catch (e: any) {
      if (lastReqIdRef.current !== reqId) return;
      setError(e.message || 'Ukjent feil');
      setData([]);
      setTotal(0);
    } finally {
      if (lastReqIdRef.current === reqId) setLoading(false);
    }
  }, [orgnrQuery, valueQuery, fieldQuery, page]);

  // Debounce queries so we don't spam the API while user is typing
  const debouncedFetch = useMemo(() => debounce(fetchData, 400), [fetchData]);
  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [orgnrQuery, valueQuery, fieldQuery]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const gotoPage = (p: number) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    if (clamped !== page) setPage(clamped);
  };

  // Manual trigger handlers
  const handleOpenTriggerModal = () => {
    setShowTriggerModal(true);
    setTriggerInput('');
    setTriggerError(null);
    setTriggerSuccess(false);
  };

  const handleCloseTriggerModal = () => {
    setShowTriggerModal(false);
    setTriggerInput('');
    setTriggerError(null);
    setTriggerSuccess(false);
  };

  const handleTriggerSubmit = async () => {
    setTriggerError(null);
    setTriggerSuccess(false);

    // Parse input: split by newlines and/or commas, trim, filter out empty
    const orgNrs = triggerInput
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (orgNrs.length === 0) {
      setTriggerError('Ingen organisasjonsnummer funnet. Skriv inn minst ett orgnr.');
      return;
    }

    setTriggerLoading(true);
    try {
      await triggerBrregUpdate(orgNrs);
      setTriggerSuccess(true);
      setTriggerInput('');
    } catch (e: any) {
      setTriggerError(e.message || 'Feil ved sending av orgnr');
    } finally {
      setTriggerLoading(false);
    }
  };

  // Unique field types from current result set + presets for dropdown
  const fieldOptions = useMemo(() => {
    const preset = [
      'DagligLeder',
      'Navn',
      'Forretningsadresse',
      'Postadresse',
      'Hjemmeside',
      'Epost',
      'Telefon',
      'Status',
    ];
    const set = new Set<string>(preset);
    for (const it of data) {
      if (it.field && it.field.trim()) set.add(it.field.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'nb'));
  }, [data]);

  return (
    <Container fluid>
      <PageHeader>Brreg oppdateringer</PageHeader>
      <p className="text-muted" style={{ maxWidth: 760 }}>
        Oversikt over endringer detektert ved polling av Brreg sitt oppdaterings-API. Du kan søke på
        organisasjonsnummer og verdier (både tidligere og nye verdier), og filtrere på felttype
        (propertyName) via nedtrekkslisten.
      </p>

      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <h5 className="mb-2">Brreg jobber</h5>
          {dashboard?.checkedAt && (
            <div className="small text-muted mb-2">
              Sjekket: {new Date(dashboard.checkedAt).toLocaleString('nb-NO')}
            </div>
          )}
        </div>

        {runNowError && <div className="alert alert-danger py-2 mb-2">{runNowError}</div>}

        {dashboardLoading && (
          <div className="small text-muted">
            <Spinner as="span" animation="border" size="sm" className="me-1" /> Laster dashboard...
          </div>
        )}

        {dashboardError && (
          <div className="alert alert-danger py-2 mb-2">
            Feil ved lasting av dashboard: {dashboardError}
          </div>
        )}

        {!dashboardLoading && dashboard?.jobs?.length ? (
          <div className="table-responsive">
            <Table striped hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>Jobb</th>
                  <th className="text-center">Aktiv</th>
                  <th className="text-center">Lookback</th>
                  <th className="text-center">Page size</th>
                  <th>Cursor</th>
                  <th className="text-end">Sist oppd.-id</th>
                  <th className="text-end">Fetched</th>
                  <th className="text-end">Matched</th>
                  <th className="text-end">Auto</th>
                  <th className="text-end">QA</th>
                  <th className="text-center">Kjør</th>
                  <th className="text-center">Detaljer</th>
                  <th className="text-center">Overrides</th>
                  <th className="text-center">Siste kjøring</th>
                  <th className="text-end">Timer siden suksess</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.jobs.map((job) => {
                  const enabled = job?.effectiveSettings?.enabled;
                  const lookbackDays = job?.effectiveSettings?.lookbackDays;
                  const pageSizeValue = job?.effectiveSettings?.pageSize;
                  const cursorMode = job?.effectiveSettings?.cursorMode;
                  const lastOppdateringsId = job?.effectiveSettings?.lastOppdateringsId;

                  const overrideCount = job?.overrides
                    ? Object.values(job.overrides).filter((v) => v !== null && v !== undefined)
                        .length
                    : 0;

                  const lastRunSucceeded = job?.lastRun?.succeeded;
                  const lastRunFinishedAt = job?.lastRun?.finishedAt
                    ? new Date(job.lastRun.finishedAt).toLocaleString('nb-NO')
                    : null;

                  const fetchedCount = job?.lastRun?.fetchedCount;
                  const matchedCount = job?.lastRun?.matchedCount;
                  const autoUpdatedCount = job?.lastRun?.autoUpdatedCount;
                  const qaMessagesSentCount = job?.lastRun?.qaMessagesSentCount;

                  const hoursSince =
                    typeof job?.hoursSinceLastSuccess === 'number'
                      ? Math.round(job.hoursSinceLastSuccess * 10) / 10
                      : null;

                  return (
                    <tr key={job.jobName}>
                      <td className="text-nowrap fw-semibold">{job.jobName}</td>
                      <td className="text-center">
                        {enabled === true ? (
                          <Badge bg="success">Ja</Badge>
                        ) : enabled === false ? (
                          <Badge bg="secondary">Nei</Badge>
                        ) : (
                          <span className="text-muted small">(ukjent)</span>
                        )}
                      </td>
                      <td className="text-center">{lookbackDays ?? '-'}</td>
                      <td className="text-center">{pageSizeValue ?? '-'}</td>
                      <td className="text-nowrap">{cursorMode || '-'}</td>
                      <td className="text-end">{lastOppdateringsId ?? '-'}</td>
                      <td className="text-end">
                        {typeof fetchedCount === 'number' ? fetchedCount : '-'}
                      </td>
                      <td className="text-end">
                        {typeof matchedCount === 'number' ? matchedCount : '-'}
                      </td>
                      <td className="text-end">
                        {typeof autoUpdatedCount === 'number' ? autoUpdatedCount : '-'}
                      </td>
                      <td className="text-end">
                        {typeof qaMessagesSentCount === 'number' ? qaMessagesSentCount : '-'}
                      </td>
                      <td className="text-center">
                        {supportedJobNames.has(job.jobName) ? (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={runNowLoadingJob === job.jobName}
                            onClick={() => handleOpenRunNowModal(job.jobName)}
                          >
                            {runNowLoadingJob === job.jobName ? (
                              <>
                                <Spinner as="span" animation="border" size="sm" className="me-1" />
                                Starter...
                              </>
                            ) : (
                              'Kjør nå'
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => handleOpenRunsModal(job.jobName)}
                        >
                          Historikk
                        </Button>
                      </td>
                      <td className="text-center">
                        {overrideCount > 0 ? (
                          <Badge bg="warning" text="dark">
                            {overrideCount}
                          </Badge>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {lastRunSucceeded === true ? (
                          <Badge bg="success">OK</Badge>
                        ) : lastRunSucceeded === false ? (
                          <Badge bg="danger">Feil</Badge>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                        {lastRunFinishedAt && (
                          <div className="small text-muted">{lastRunFinishedAt}</div>
                        )}
                      </td>
                      <td className="text-end">{hoursSince !== null ? `${hoursSince} t` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : !dashboardLoading && !dashboardError ? (
          <div className="small text-muted">Ingen jobber funnet.</div>
        ) : null}
      </div>

      <Form className="mb-3 mt-3">
        <Row className="g-2">
          <Col md={3} sm={6} xs={12}>
            <Form.Label>Organisasjonsnummer</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Søk orgnr..."
              value={orgnrQuery}
              onChange={(e) => setOrgnrQuery(RemoveSpaceAndDot(e.target.value))}
            />
          </Col>
          <Col md={3} sm={6} xs={12}>
            <Form.Label>Verdi (ny / gammel)</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Søk i newValue / oldValue..."
              value={valueQuery}
              onChange={(e) => setValueQuery(e.target.value)}
            />
          </Col>
          <Col md={3} sm={6} xs={12}>
            <Form.Label>Felttype</Form.Label>
            <Form.Select
              size="sm"
              value={fieldQuery}
              onChange={(e) => setFieldQuery(e.target.value)}
            >
              <option value="">Alle felt</option>
              {fieldOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2} sm={6} xs={12} className="d-flex align-items-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setOrgnrQuery('');
                setValueQuery('');
                setFieldQuery('');
              }}
              disabled={loading}
            >
              Nullstill
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleOpenTriggerModal}
              title="Trigger Brreg-oppdatering manuelt"
            >
              Trigger
            </Button>
          </Col>
          <Col md={1} sm={6} xs={12} className="d-flex align-items-end justify-content-md-end">
            {loading && (
              <div>
                <Spinner animation="border" size="sm" />{' '}
                <span className="ms-1 small">Laster...</span>
              </div>
            )}
            {!loading && <div className="small text-muted">{total} treff</div>}
          </Col>
        </Row>
      </Form>

      {error && <div className="alert alert-danger py-2">Feil ved lasting: {error}</div>}

      <div className="table-responsive">
        <Table striped hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Enhet</th>
              <th>Endring</th>
              <th>Endret</th>
              <th className="text-center">Aktive enheter</th>
              <th className="text-center">Aktivt kjedekontor</th>
              <th className="text-center">Auto-oppdatert</th>
              <th className="text-center">Sendt til QA</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted small py-3">
                  Ingen treff
                </td>
              </tr>
            )}
            {data.map((row) => {
              const changedDate = row.changedAtUtc ? new Date(row.changedAtUtc) : null;
              const dateStr =
                changedDate && !isNaN(changedDate.getTime())
                  ? changedDate.toLocaleString('nb-NO')
                  : '';
              const hasActions = Array.isArray(row.actionLog) && row.actionLog.length > 0;
              const popover = hasActions ? (
                <Popover id={`popover-actions-${row.id}`} style={{ maxWidth: 420 }}>
                  <Popover.Header as="h6" className="py-2">
                    Utførte handlinger ({row.actionLog!.length})
                  </Popover.Header>
                  <Popover.Body style={{ padding: '0.5rem' }}>
                    <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                      <ol className="mb-0 ps-3 small">
                        {row.actionLog!.map((a, idx) => (
                          <li key={idx} className="mb-1">
                            {a}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </Popover.Body>
                </Popover>
              ) : null;
              return (
                <tr key={row.id}>
                  <td className="text-nowrap">
                    <div className="fw-semibold">
                      <a
                        href={`https://kjederegisteretadmin.tradesolution.no/enheter?criteria=${row.orgNr}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Søk etter enhet i Kjederegisteretadmin"
                      >
                        {row.orgNr}
                      </a>{' '}
                      <a
                        href={`https://w2.brreg.no/kunngjoring/hent_nr.jsp?orgnr=${row.orgNr}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="small"
                        title="Se kunngjøringer i Brreg"
                      >
                        ⓘ
                      </a>
                    </div>
                    <div className="small text-muted">{row.juridiskNavn || '(ukjent navn)'}</div>
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    <div className="small text-uppercase">
                      {row.field ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-muted text-decoration-none"
                          onClick={() => setFieldQuery(row.field!)}
                          title="Filtrer på denne felttypen"
                        >
                          {row.field}
                        </button>
                      ) : (
                        <span className="text-muted">(felt)</span>
                      )}
                    </div>
                    <div className="small">
                      {row.oldValue ? (
                        <span className="text-muted">{row.oldValue}</span>
                      ) : (
                        <span className="text-muted">(tom)</span>
                      )}{' '}
                      →{' '}
                      {row.newValue ? (
                        <span className="fw-semibold">{row.newValue}</span>
                      ) : (
                        <span className="text-muted">(tom)</span>
                      )}
                    </div>
                  </td>
                  <td className="text-nowrap small">{dateStr}</td>
                  <td className="text-center">{row.activeEnhetCount}</td>
                  <td className="text-center">
                    {row.hovedkontorEnhetId ? (
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/enheter/${row.hovedkontorEnhetId}`}
                      >
                        Ja
                      </a>
                    ) : (
                      'Nei'
                    )}
                  </td>
                  <td className="text-center">{row.antallEnheterAutomaticallyUpdated ?? 0}</td>
                  <td className="text-center">{row.antallEnheterSentToQA ?? 0}</td>
                  <td className="text-center">
                    {hasActions ? (
                      <OverlayTrigger
                        trigger={['click']}
                        rootClose
                        placement="left"
                        overlay={popover!}
                      >
                        <Button size="sm" variant="outline-primary">
                          Logg
                          <span className="visually-hidden"> handlinger for {row.orgNr}</span>
                        </Button>
                      </OverlayTrigger>
                    ) : (
                      <span className="text-muted small">(ingen)</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2 mb-4">
        <div className="small text-muted">
          Side {page} av {totalPages}
        </div>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => gotoPage(page - 1)}
            disabled={page <= 1 || loading}
          >
            Forrige
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => gotoPage(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Neste
          </Button>
        </div>
      </div>

      {/* Run Now Modal */}
      <Modal show={showRunNowModal} onHide={handleCloseRunNowModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Kjør jobb: {runNowModalJob}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Jobben vil hente oppdateringer fra Brreg. Du kan velge en dato for å hente fra et
            bestemt tidspunkt (overstyrer checkpoint). Uten valgt dato brukes eksisterende cursor
            eller standard lookback.
          </p>
          <Form.Group>
            <Form.Label>Fra dato (valgfritt)</Form.Label>
            <NorwegianDatepicker
              value={runNowModalDate}
              onChange={(date) => setRunNowModalDate(date)}
              maxDate={new Date()}
              showLastDaySuggestions
            />
          </Form.Group>
          {runNowModalDate && (
            <div className="small text-muted mt-2">
              Henter oppdateringer fra og med {runNowModalDate.toLocaleDateString('nb-NO')}.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRunNowModal}>
            Avbryt
          </Button>
          <Button
            variant="primary"
            onClick={handleRunNowConfirm}
            disabled={runNowLoadingJob === runNowModalJob}
          >
            Kjør nå
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Job Run History Modal */}
      <Modal show={showRunsModal} onHide={handleCloseRunsModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Kjørings-historikk: {runsModalJob}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {jobRunsLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Laster kjøringer...
            </div>
          )}
          {jobRunsError && <div className="alert alert-danger py-2">{jobRunsError}</div>}
          {!jobRunsLoading && !jobRunsError && jobRuns.length === 0 && (
            <div className="text-muted small text-center py-3">Ingen kjøringer funnet.</div>
          )}
          {!jobRunsLoading && jobRuns.length > 0 && (
            <div className="table-responsive">
              <Table striped hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th></th>
                    <th>Startet</th>
                    <th>Ferdig</th>
                    <th className="text-end">Fetched</th>
                    <th className="text-end">Matched</th>
                    <th className="text-end">Auto</th>
                    <th className="text-end">QA</th>
                    <th className="text-end">Skippet</th>
                    <th className="text-end">Feil</th>
                    <th className="text-end">Max oppd.-id</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {jobRuns.map((run, idx) => {
                    const key = run.id ?? String(idx);
                    const runId = run.id ?? '';
                    const startStr = run.startedAt
                      ? new Date(run.startedAt).toLocaleString('nb-NO')
                      : '-';
                    const finishStr = run.finishedAt
                      ? new Date(run.finishedAt).toLocaleString('nb-NO')
                      : '-';
                    const hasLog = Array.isArray(run.logEntries) && run.logEntries.length > 0;
                    const isEnhetUpdates = runsModalJob === 'brreg-enhet-updates';
                    const canExpand = hasLog || (isEnhetUpdates && !!runId);
                    const isExpanded = expandedRunIds.has(key);
                    const isLoadingOutcomes = outcomesLoading.has(runId);
                    const outcomes = outcomesCache[runId];
                    return (
                      <React.Fragment key={key}>
                        <tr>
                          <td className="text-center" style={{ width: 28 }}>
                            {canExpand ? (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                title={isExpanded ? 'Skjul logg' : 'Vis logg'}
                                onClick={() => toggleRunExpanded(key, runsModalJob ?? '', runId)}
                              >
                                {isExpanded ? '▾' : '▸'}
                              </Button>
                            ) : null}
                          </td>
                          <td className="text-nowrap small">{startStr}</td>
                          <td className="text-nowrap small">{finishStr}</td>
                          <td className="text-end">{run.fetchedCount ?? '-'}</td>
                          <td className="text-end">{run.matchedCount ?? '-'}</td>
                          <td className="text-end">{run.autoUpdatedCount ?? '-'}</td>
                          <td className="text-end">{run.qaMessagesSentCount ?? '-'}</td>
                          <td className="text-end">{run.skippedCount ?? '-'}</td>
                          <td className="text-end">
                            {typeof run.errorCount === 'number' && run.errorCount > 0 ? (
                              <span className="text-danger fw-semibold">{run.errorCount}</span>
                            ) : (
                              (run.errorCount ?? '-')
                            )}
                          </td>
                          <td className="text-end">{run.maxOppdateringsIdSeen ?? '-'}</td>
                          <td className="small" style={{ maxWidth: 260 }}>
                            {run.errorMessage ? (
                              <span className="text-danger">{run.errorMessage}</span>
                            ) : (
                              (run.outcome ?? '-')
                            )}
                          </td>
                        </tr>
                        {isExpanded &&
                          (isEnhetUpdates ? (
                            <tr>
                              <td colSpan={11} style={{ padding: 0, background: '#f8f9fa' }}>
                                <div style={{ padding: '0.4rem 0.8rem 0.6rem 2rem' }}>
                                  {isLoadingOutcomes && (
                                    <div className="text-muted small">
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-1"
                                      />{' '}
                                      Laster utfall…
                                    </div>
                                  )}
                                  {!isLoadingOutcomes &&
                                    outcomes &&
                                    outcomes.entries.length === 0 &&
                                    !hasLog && (
                                      <div className="text-muted small">
                                        Ingen feltendringer registrert for denne kjøringen.
                                      </div>
                                    )}
                                  {!isLoadingOutcomes &&
                                    outcomes &&
                                    outcomes.entries.length === 0 &&
                                    hasLog && (
                                      <>
                                        <div className="text-muted small mb-1">
                                          Ingen feltendringer registrert. Viser innsendte
                                          oppdateringer (orgnr-nivå):
                                        </div>
                                        {run.logEntriesTruncated && (
                                          <div className="text-warning small mb-1">
                                            ⚠ Loggen er avkortet (viser maks 500 per kjøring)
                                          </div>
                                        )}
                                        <Table
                                          size="sm"
                                          bordered={false}
                                          className="mb-0"
                                          style={{ fontSize: '0.78rem' }}
                                        >
                                          <thead>
                                            <tr className="text-muted">
                                              <th style={{ width: 110 }}>OrgNr</th>
                                              <th style={{ width: 120 }}>Handling</th>
                                              <th style={{ width: 160 }}>Registrert i Brreg</th>
                                              <th>Detalj</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(run.logEntries as BrregJobRunLogEntry[]).map(
                                              (entry, ei) => (
                                                <tr key={ei}>
                                                  <td className="text-nowrap">{entry.orgNr}</td>
                                                  <td>
                                                    <span className="text-muted">
                                                      {entry.action}
                                                    </span>
                                                  </td>
                                                  <td className="text-nowrap text-muted">
                                                    {entry.brregEventTime
                                                      ? new Date(
                                                          entry.brregEventTime,
                                                        ).toLocaleString('nb-NO')
                                                      : '-'}
                                                  </td>
                                                  <td className="text-muted">
                                                    {entry.detail ?? '-'}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </Table>
                                      </>
                                    )}
                                  {!isLoadingOutcomes &&
                                    outcomes &&
                                    outcomes.entries.length > 0 && (
                                      <Table
                                        size="sm"
                                        bordered={false}
                                        className="mb-0"
                                        style={{ fontSize: '0.78rem' }}
                                      >
                                        <thead>
                                          <tr className="text-muted">
                                            <th style={{ width: 110 }}>OrgNr</th>
                                            <th>Navn</th>
                                            <th style={{ width: 130 }}>Felt</th>
                                            <th style={{ width: 120 }}>Handling</th>
                                            <th style={{ width: 160 }}>Registrert i Brreg</th>
                                            <th>Detalj</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {[...outcomes.entries]
                                            .sort((a, b) => a.orgNr.localeCompare(b.orgNr))
                                            .map((entry, ei) => (
                                              <tr key={ei}>
                                                <td className="text-nowrap">{entry.orgNr}</td>
                                                <td>{entry.juridiskNavn ?? '-'}</td>
                                                <td className="text-muted">{entry.propertyName}</td>
                                                <td>
                                                  <span
                                                    className={
                                                      entry.action === 'AutoUpdated'
                                                        ? 'text-success'
                                                        : entry.action === 'SentToQA'
                                                          ? 'text-primary'
                                                          : entry.action === 'Error'
                                                            ? 'text-danger'
                                                            : 'text-muted'
                                                    }
                                                  >
                                                    {entry.action}
                                                  </span>
                                                </td>
                                                <td className="text-nowrap text-muted">
                                                  {entry.brregEventTime
                                                    ? new Date(entry.brregEventTime).toLocaleString(
                                                        'nb-NO',
                                                      )
                                                    : '-'}
                                                </td>
                                                <td className="text-muted">
                                                  {entry.detail ?? '-'}
                                                </td>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </Table>
                                    )}
                                </div>
                              </td>
                            </tr>
                          ) : (
                            isExpanded &&
                            hasLog && (
                              <tr>
                                <td colSpan={11} style={{ padding: 0, background: '#f8f9fa' }}>
                                  <div style={{ padding: '0.4rem 0.8rem 0.6rem 2rem' }}>
                                    {run.logEntriesTruncated && (
                                      <div className="text-warning small mb-1">
                                        ⚠ Loggen er avkortet (viser maks 500 per kjøring)
                                      </div>
                                    )}
                                    <Table
                                      size="sm"
                                      bordered={false}
                                      className="mb-0"
                                      style={{ fontSize: '0.78rem' }}
                                    >
                                      <thead>
                                        <tr className="text-muted">
                                          <th style={{ width: 110 }}>OrgNr</th>
                                          <th>Navn</th>
                                          <th style={{ width: 120 }}>Handling</th>
                                          <th style={{ width: 160 }}>Registrert i Brreg</th>
                                          {runsModalJob === 'brreg-nye-enheter-nace' && (
                                            <>
                                              <th style={{ width: 90 }}>Nace 1</th>
                                              <th style={{ width: 90 }}>Nace 2</th>
                                              <th style={{ width: 70 }}>Org.form</th>
                                            </>
                                          )}
                                          <th>Detalj</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(run.logEntries as BrregJobRunLogEntry[]).map(
                                          (entry, ei) => (
                                            <tr key={ei}>
                                              <td className="text-nowrap">{entry.orgNr}</td>
                                              <td>{entry.juridiskNavn ?? '-'}</td>
                                              <td>
                                                <span
                                                  className={
                                                    entry.action === 'AutoUpdated'
                                                      ? 'text-success'
                                                      : entry.action === 'SentToQA'
                                                        ? 'text-primary'
                                                        : entry.action === 'Error'
                                                          ? 'text-danger'
                                                          : 'text-muted'
                                                  }
                                                >
                                                  {entry.action}
                                                </span>
                                              </td>
                                              <td className="text-nowrap text-muted">
                                                {entry.brregEventTime
                                                  ? new Date(entry.brregEventTime).toLocaleString(
                                                      'nb-NO',
                                                    )
                                                  : '-'}
                                              </td>
                                              {runsModalJob === 'brreg-nye-enheter-nace' && (
                                                <>
                                                  <td className="text-muted">
                                                    {entry.naeringskode1 ?? '-'}
                                                  </td>
                                                  <td className="text-muted">
                                                    {entry.naeringskode2 ?? '-'}
                                                  </td>
                                                  <td className="text-muted">
                                                    {entry.organisasjonsform ?? '-'}
                                                  </td>
                                                </>
                                              )}
                                              <td className="text-muted">{entry.detail ?? '-'}</td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </Table>
                                  </div>
                                </td>
                              </tr>
                            )
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRunsModal}>
            Lukk
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manual Trigger Modal */}
      <Modal show={showTriggerModal} onHide={handleCloseTriggerModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Trigger Brreg-oppdatering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-2">
            Skriv inn ett eller flere organisasjonsnummer (ett per linje eller kommaseparert).
          </p>
          <Form.Control
            as="textarea"
            rows={6}
            placeholder="123456789&#10;987654321&#10;111222333"
            value={triggerInput}
            onChange={(e) => setTriggerInput(e.target.value)}
            disabled={triggerLoading}
          />
          {triggerError && <div className="alert alert-danger mt-2 mb-0 py-2">{triggerError}</div>}
          {triggerSuccess && (
            <div className="alert alert-success mt-2 mb-0 py-2">
              Organisasjonsnumrene ble sendt for oppdatering!
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTriggerModal} disabled={triggerLoading}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleTriggerSubmit} disabled={triggerLoading}>
            {triggerLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Sender...
              </>
            ) : (
              'Send'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BrregOppdateringerPage;
